import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { barterRequestApi } from '@/services/api'
import { useAuthStore } from '@/store/auth.store'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import Loading from '@/components/ui/Loading'
import Alert from '@/components/ui/Alert'
import Modal from '@/components/ui/Modal'
import ResponsiveTable from '@/components/ui/ResponsiveTable'
import { formatDate } from '@/utils/format'
import { CheckCircle, XCircle, Eye, Clock, ThumbsUp, ThumbsDown } from 'lucide-react'

interface BarterRequest {
  id: number
  requester_barter_id: number
  target_barter_id: number
  group_id?: number
  status: 'pending' | 'accepted' | 'rejected'
  created_at: string
  updated_at: string
  requester_barter_title: string
  target_barter_title: string
  first_name: string
  last_name: string
}

export default function BarterRequests() {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all')
  const [selectedRequest, setSelectedRequest] = useState<BarterRequest | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['barter-requests'],
    queryFn: () => barterRequestApi.list(),
  })

  const updateRequestMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      barterRequestApi.update(id, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['barter-requests'] })
      setShowDetailModal(false)
      setSelectedRequest(null)
      const action = variables.status === 'accepted' ? 'accepted' : 'rejected'
      setSuccess(`Barter request ${action} successfully!`)
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update request')
    },
  })

  const requests: BarterRequest[] = (data?.data as any)?.requests || []
  const filteredRequests = filter === 'all'
    ? requests
    : requests.filter((r: BarterRequest) => r.status === filter)

  // Separate incoming and outgoing requests
  const incomingRequests = filteredRequests.filter((r: BarterRequest) => {
    // Find the target barter's owner (need to check via user_id, but we have requester info)
    // Incoming = requests where I own the target barter
    // We can infer this if the requester is NOT the current user
    return r.first_name !== user?.first_name || r.last_name !== user?.last_name
  })

  const outgoingRequests = filteredRequests.filter((r: BarterRequest) => {
    // Outgoing = requests where I am the requester
    return r.first_name === user?.first_name && r.last_name === user?.last_name
  })

  const handleAccept = (request: BarterRequest) => {
    if (window.confirm('Accept this barter request and create a group?')) {
      updateRequestMutation.mutate({ id: request.id, status: 'accepted' })
    }
  }

  const handleReject = (request: BarterRequest) => {
    if (window.confirm('Reject this barter request?')) {
      updateRequestMutation.mutate({ id: request.id, status: 'rejected' })
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="warning">Pending</Badge>
      case 'accepted':
        return <Badge variant="success">Accepted</Badge>
      case 'rejected':
        return <Badge variant="danger">Rejected</Badge>
      default:
        return <Badge variant="gray">{status}</Badge>
    }
  }

  if (isLoading) return <Loading />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Barter Requests</h1>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Filter Tabs */}
      <div className="flex space-x-2 border-b">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'all'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          All ({requests.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'pending'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Clock className="w-4 h-4 inline mr-1" />
          Pending ({requests.filter((r: BarterRequest) => r.status === 'pending').length})
        </button>
        <button
          onClick={() => setFilter('accepted')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'accepted'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <CheckCircle className="w-4 h-4 inline mr-1" />
          Accepted ({requests.filter((r: BarterRequest) => r.status === 'accepted').length})
        </button>
        <button
          onClick={() => setFilter('rejected')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            filter === 'rejected'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <XCircle className="w-4 h-4 inline mr-1" />
          Rejected ({requests.filter((r: BarterRequest) => r.status === 'rejected').length})
        </button>
      </div>

      {/* Incoming Requests */}
      {incomingRequests.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold mb-4">Incoming Requests</h2>
          <p className="text-sm text-gray-500 mb-4">
            Requests from other users who want to trade with your barters
          </p>
          <ResponsiveTable>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Their Barter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Your Barter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {incomingRequests.map((request: BarterRequest) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {request.first_name} {request.last_name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.requester_barter_title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.target_barter_title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => {
                            setSelectedRequest(request)
                            setShowDetailModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAccept(request)}
                              className="text-green-600 hover:text-green-900"
                              title="Accept"
                            >
                              <ThumbsUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(request)}
                              className="text-red-600 hover:text-red-900"
                              title="Reject"
                            >
                              <ThumbsDown className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        </Card>
      )}

      {/* Outgoing Requests */}
      {outgoingRequests.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold mb-4">Outgoing Requests</h2>
          <p className="text-sm text-gray-500 mb-4">
            Requests you've sent to other users
          </p>
          <ResponsiveTable>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">To</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Your Barter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Their Barter</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {outgoingRequests.map((request: BarterRequest) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">Awaiting response...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.requester_barter_title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{request.target_barter_title}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(request.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedRequest(request)
                          setShowDetailModal(true)
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ResponsiveTable>
        </Card>
      )}

      {/* Empty State */}
      {filteredRequests.length === 0 && (
        <Card>
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">
              {filter === 'all'
                ? 'No barter requests yet'
                : `No ${filter} requests`}
            </p>
            <p className="text-sm text-gray-400">
              Start a barter group from the barters list to create a request
            </p>
          </div>
        </Card>
      )}

      {/* Request Detail Modal */}
      {showDetailModal && selectedRequest && (
        <Modal
          title="Barter Request Details"
          onClose={() => {
            setShowDetailModal(false)
            setSelectedRequest(null)
          }}
          size="lg"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">From</h3>
                <p className="mt-1 text-gray-900">
                  {selectedRequest.first_name} {selectedRequest.last_name}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Status</h3>
                <div className="mt-1">{getStatusBadge(selectedRequest.status)}</div>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Their Barter</h3>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-medium text-blue-900">{selectedRequest.requester_barter_title}</p>
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Your Barter</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="font-medium text-green-900">{selectedRequest.target_barter_title}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500">Requested</h3>
                <p className="mt-1 text-sm text-gray-900">{formatDate(selectedRequest.created_at)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500">Last Updated</h3>
                <p className="mt-1 text-sm text-gray-900">{formatDate(selectedRequest.updated_at)}</p>
              </div>
            </div>

            {selectedRequest.status === 'accepted' && selectedRequest.group_id && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <CheckCircle className="w-4 h-4 inline mr-2" />
                  This request was accepted and a barter group was created (Group #{selectedRequest.group_id})
                </p>
              </div>
            )}

            {error && (
              <Alert type="error" message={error} onClose={() => setError('')} />
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              {selectedRequest.status === 'pending' &&
               selectedRequest.first_name !== user?.first_name && (
                <>
                  <Button
                    variant="danger"
                    onClick={() => handleReject(selectedRequest)}
                    isLoading={updateRequestMutation.isPending}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    variant="success"
                    onClick={() => handleAccept(selectedRequest)}
                    isLoading={updateRequestMutation.isPending}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Accept & Create Group
                  </Button>
                </>
              )}
              {(selectedRequest.status !== 'pending' ||
                selectedRequest.first_name === user?.first_name) && (
                <Button onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
